import { useState } from 'react'
import { useSession } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { createDocument, updateDocument } from '@/features/documents/api'
import {
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_ORDER,
  DEFAULT_DOCUMENT_TYPE,
} from '../documentTypes'
import { AccessEditor } from '@/components/AccessEditor'
import type { DocumentRecord, DocumentType } from '../types'

export interface DocumentDetailsInitial {
  name: string
  type: DocumentType
  access: string[]
  description: string
}

export interface DocumentDetailsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  /** Edit mode: the document id to update. Required when mode === 'edit'. */
  documentId?: string
  /** Create mode: the agent the new document belongs to. Required when mode === 'create'. */
  agentId?: string
  /** Prefilled fields (edit mode) or seed defaults (create mode). */
  initial?: Partial<DocumentDetailsInitial>
  /** Default type in create mode when `initial.type` is absent. */
  defaultType?: DocumentType
  /** Called with the saved record so the consumer can navigate/refresh. */
  onSubmitted: (record: DocumentRecord) => void
}

export function DocumentDetailsModal({
  open,
  onOpenChange,
  mode,
  documentId,
  agentId,
  initial,
  defaultType = DEFAULT_DOCUMENT_TYPE,
  onSubmitted,
}: DocumentDetailsModalProps) {
  const { session } = useSession()
  const queryClient = useQueryClient()

  const [name, setName] = useState(initial?.name ?? '')
  const [type, setType] = useState<DocumentType>(initial?.type ?? defaultType)
  const [access, setAccess] = useState<string[]>(initial?.access ?? [])
  const [description, setDescription] = useState(initial?.description ?? '')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Re-seed form each time the modal opens so it reflects the latest record.
  const [seededFor, setSeededFor] = useState(false)
  if (open && !seededFor) {
    setName(initial?.name ?? '')
    setType(initial?.type ?? defaultType)
    setAccess(initial?.access ?? [])
    setDescription(initial?.description ?? '')
    setError(null)
    setSeededFor(true)
  } else if (!open && seededFor) {
    setSeededFor(false)
  }

  const isValid = name.trim().length > 0

  const handleSubmit = async () => {
    if (!isValid) {
      setError('Name and type are required')
      return
    }
    setError(null)
    setIsSubmitting(true)
    try {
      const token = await session?.getToken()
      if (!token) throw new Error('No authentication token available')

      const metadataFields = {
        name: name.trim(),
        description,
        access,
        metadata: { type },
      }

      let record: DocumentRecord
      if (mode === 'edit') {
        if (!documentId) throw new Error('Missing document id')
        // Preserve existing content: send only the metadata the modal owns.
        record = await updateDocument(documentId, metadataFields, token)
      } else {
        if (!agentId) throw new Error('Missing agent id')
        // Create with empty body; content is authored in the editor afterwards.
        record = await createDocument(
          agentId,
          { ...metadataFields, data: '' },
          token
        )
      }

      await queryClient.invalidateQueries({ queryKey: ['documents'] })
      toast.success(mode === 'edit' ? 'Document updated' : 'Document created')
      onOpenChange(false)
      onSubmitted(record)
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : `Failed to ${mode === 'edit' ? 'update' : 'create'} document`
      setError(msg)
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[504px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Edit details' : 'New document'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'edit'
              ? 'Update this document’s details.'
              : 'Set up your document. You can add content next.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="doc-name">Name</Label>
            <Input
              id="doc-name"
              value={name}
              placeholder="Document name"
              onChange={(e) => setName(e.target.value)}
              aria-invalid={Boolean(error) && !name.trim()}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="doc-type">Type</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as DocumentType)}>
              <SelectTrigger id="doc-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPE_ORDER.map((value) => {
                  const {
                    label,
                    icon: Icon,
                    colorClass,
                  } = DOCUMENT_TYPES[value]
                  return (
                    <SelectItem key={value} value={value}>
                      <Icon className={`size-4 ${colorClass}`} />
                      {label}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          <AccessEditor id="doc-access" value={access} onChange={setAccess} />

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="doc-description">Description</Label>
            <Textarea
              id="doc-description"
              value={description}
              placeholder="What is this document for?"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            {isSubmitting ? 'Saving…' : mode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
